// Pure utility functions extracted from DeckBuilder

export const RARITY_ORDER = ["Legendary Rare", "Secret Rare", "Super Rare", "Rare", "Uncommon", "Common", "Promo", "Starter"];

export const UNLIMITED_CARDS = ["Bowpawn"];

export function isUnlimitedCard(card) {
  const name = card.cardName || card.name || "";
  return UNLIMITED_CARDS.includes(name);
}

const DUAL_SIDED_CARDS = {
  "Synthetic Laboratory": "Xeno",
  "Xeno": "Synthetic Laboratory",
};

export function getDualSideName(card) {
  const name = card.cardName || card.name || "";
  return DUAL_SIDED_CARDS[name] || null;
}

export function getUniqueCards(deckCards) {
  const seen = new Map();
  for (const card of deckCards) {
    if (!seen.has(card._id)) {
      seen.set(card._id, { card, count: 1 });
    } else {
      seen.get(card._id).count++;
    }
  }
  return Array.from(seen.values());
}

export function getDeckStats(deckCards) {
  const nonFieldCards = deckCards.filter(c => c.cardType !== "Field");
  const fieldCards = deckCards.filter(c => c.cardType === "Field");

  // Deduplicate dual-sided field pairs (e.g. Synthetic Laboratory + Xeno = 1 field slot)
  const fieldNames = new Set(fieldCards.map(c => c.cardName || c.name));
  let fieldCount = fieldNames.size;
  for (const name of fieldNames) {
    const partner = DUAL_SIDED_CARDS[name];
    if (partner && fieldNames.has(partner)) {
      fieldCount--;
      break; // only one pair exists, so break after adjusting once
    }
  }

  return {
    total: nonFieldCards.length,
    characters: deckCards.filter(c => c.cardType === "Character").length,
    strategies: deckCards.filter(c => c.cardType === "Strategy").length,
    abilities: deckCards.filter(c => c.cardType === "Ability").length,
    field: fieldCount,
  };
}

export function getCardCount(deckCards, cardId) {
  return deckCards.filter(c => c._id === cardId).length;
}

export function getExportText(deckCards, sideDeckCards = []) {
  const uniqueCards = getUniqueCards(deckCards);
  if (uniqueCards.length === 0 && sideDeckCards.length === 0) return "";

  const typeOrder = ["Character", "Strategy", "Ability", "Field"];
  const typeLabels = { Character: "Characters", Strategy: "Strategies", Ability: "Abilities", Field: "Field" };
  const lines = [];

  for (const type of typeOrder) {
    const group = uniqueCards.filter(u => u.card.cardType === type);
    if (group.length === 0) continue;
    if (lines.length > 0) lines.push("");
    lines.push(`# ${typeLabels[type]}`);
    for (const { card, count } of group) {
      const num = card.cardNumber ? ` - ${card.cardNumber}` : "";
      lines.push(`${count}x ${card.cardName || card.name}${num}`);
    }
  }

  if (sideDeckCards.length > 0) {
    const sideUnique = getUniqueCards(sideDeckCards);
    if (lines.length > 0) lines.push("");
    lines.push("# Side Deck");
    for (const { card, count } of sideUnique) {
      const num = card.cardNumber ? ` - ${card.cardNumber}` : "";
      lines.push(`${count}x ${card.cardName || card.name}${num}`);
    }
  }

  return lines.join("\n");
}

export function categorizePreviewCards(cards) {
  const categories = { characters: [], strategies: [], abilities: [], field: [] };
  const countMap = new Map();

  for (const card of cards) {
    if (!countMap.has(card._id)) {
      countMap.set(card._id, { card, count: 1 });
    } else {
      countMap.get(card._id).count++;
    }
  }

  for (const { card, count } of countMap.values()) {
    const entry = { card, count };
    switch (card.cardType) {
      case "Character": categories.characters.push(entry); break;
      case "Strategy": categories.strategies.push(entry); break;
      case "Ability": categories.abilities.push(entry); break;
      case "Field": categories.field.push(entry); break;
      default: break;
    }
  }

  categories.characters.sort((a, b) => (a.card.turnCount || 0) - (b.card.turnCount || 0));
  categories.strategies.sort((a, b) => (a.card.turnCount || 0) - (b.card.turnCount || 0));

  return categories;
}

export function getTurnCurve(cards) {
  const curve = {};
  for (const card of cards) {
    if (card.cardType === "Character" || card.cardType === "Strategy") {
      const turn = card.turnCount !== undefined ? card.turnCount : "?";
      curve[turn] = (curve[turn] || 0) + 1;
    }
  }
  return curve;
}

export function getElementDistribution(cards) {
  const elementCounts = {};
  for (const card of cards) {
    if (card.element && Array.isArray(card.element)) {
      for (const el of card.element) {
        elementCounts[el] = (elementCounts[el] || 0) + 1;
      }
    }
  }
  return elementCounts;
}

export function sortResults(results, sortBy, sortDirection, rarityOrder) {
  const sorted = [...results];

  sorted.sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case "name":
        comparison = (a.name || "").localeCompare(b.name || "");
        break;
      case "rarity":
        comparison = rarityOrder.indexOf(a.rarity) - rarityOrder.indexOf(b.rarity);
        break;
      case "turnCount": {
        const aHasTurn = a.cardType === "Character" || a.cardType === "Strategy";
        const bHasTurn = b.cardType === "Character" || b.cardType === "Strategy";
        if (aHasTurn && !bHasTurn) comparison = -1;
        else if (!aHasTurn && bHasTurn) comparison = 1;
        else if (!aHasTurn && !bHasTurn) comparison = 0;
        else comparison = (parseInt(a.turnCount, 10) || 0) - (parseInt(b.turnCount, 10) || 0);
        break;
      }
      case "cardNumber":
        comparison = (a.cardNumber || "").localeCompare(b.cardNumber || "", undefined, { numeric: true });
        break;
      default:
        comparison = 0;
    }
    return sortDirection === "asc" ? comparison : -comparison;
  });

  return sorted;
}

export function cleanCardText(text) {
  if (!text) return "";
  let cleaned = text;
  const startKeywords = ["TRIGGER", "Rush", "NECRO", "PLASMA", "FIRE", "WATER", "TERRA", "AIR", "ACTIVE", "ONGOING", "PASSIVE", "ACTIVATE"];
  let foundStart = true;
  while (foundStart) {
    foundStart = false;
    for (const keyword of startKeywords) {
      const regex = new RegExp(`^${keyword}\\s+`, "i");
      if (regex.test(cleaned)) { cleaned = cleaned.replace(regex, ""); foundStart = true; break; }
    }
  }
  cleaned = cleaned.replace(/\s*TRIGGER\s+\w+\s*\d*\s*\([^)]*\)\s*.*$/i, "");
  cleaned = cleaned.replace(/\s*Transformation icon\s*$/i, "");
  cleaned = cleaned.replace(/\s+[\w-]+\s*\|\s*[\w-]+\s*$/i, "");
  const metadataKeywords = "(GC|PLASMA|FIRE|WATER|TERRA|AIR|NECRO|ACTIVE|ONGOING|PASSIVE|ACTIVATE|TRIGGER|Green|Red|Blue|Yellow|Purple|Orange)";
  const metadataPattern = new RegExp(`(\\s+${metadataKeywords}\\s*\\d*)+\\s*$`, "gi");
  cleaned = cleaned.replace(metadataPattern, "");
  return cleaned.trim();
}

export function getSetPurchaseUrl(setName) {
  const setUrls = {
    'Awakened Primordials': 'https://shop.primaltcg.com/products/primal-tcg-1st-ed-awakened-primordials-booster-box',
    'Battlers of Bethesda': 'https://shop.primaltcg.com/products/primal-tcg-battlers-of-bethesda-booster-box',
    'Warden of the Seven Deadly Sins': 'https://shop.primaltcg.com/products/primal-tcg-warden-of-the-seven-sins-booster-box-1st-ed',
    'New Genesis': 'https://shop.primaltcg.com/products/primal-tcg-new-genesis-booster-box'
  };
  return setUrls[setName] || null;
}

export function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function getAbilityCostCount(card) {
  if (!card.abilityCost) return 0;
  let items = [];
  if (Array.isArray(card.abilityCost)) {
    items = card.abilityCost;
  } else if (typeof card.abilityCost === "string") {
    items = card.abilityCost.split(",").map(s => s.trim()).filter(s => s);
  }
  let total = 0;
  for (const item of items) {
    const lowerItem = item.toLowerCase();
    if (lowerItem === "and" || lowerItem === "or") continue;
    const num = parseInt(item, 10);
    if (!isNaN(num)) {
      total += num;
    } else {
      for (const char of lowerItem) {
        if (/[a-z]/.test(char)) total += 1;
      }
    }
  }
  return total;
}

export function getAbilityCostElements(card) {
  const elements = new Set();
  if (card.abilityCost) {
    let items = [];
    if (Array.isArray(card.abilityCost)) {
      items = card.abilityCost;
    } else if (typeof card.abilityCost === "string") {
      items = card.abilityCost.split(",").map(s => s.trim()).filter(s => s);
    }
    const ignoreWords = ["or", "and"];
    const elementMap = {
      "s": "water", "p": "plasma", "f": "fire", "t": "terra", "a": "air", "n": "necro",
      "w": "water",
      "plasma": "plasma", "fire": "fire", "water": "water",
      "terra": "terra", "air": "air", "necro": "necro",
      "blue": "water", "red": "fire", "green": "terra",
      "yellow": "air", "purple": "plasma", "black": "necro"
    };
    for (const item of items) {
      const lowerItem = item.toLowerCase();
      if (ignoreWords.includes(lowerItem)) continue;
      if (!isNaN(parseInt(item, 10))) continue;
      const mapped = elementMap[lowerItem];
      if (mapped) elements.add(mapped);
    }
  }
  return Array.from(elements);
}
